import React, { useEffect, useState, useRef } from 'react';
import { api, getToken } from '../../lib/api';
import { useSettings, resolveAsset } from '../../lib/settings';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { Save, Upload, Link2, Download, Image as ImageIcon, X, FileArchive, CreditCard, Lock, Info } from 'lucide-react';

const PAYMENT_DEFAULTS = {
  enabled: false,
  defaultProvider: 'stripe',
  currency: 'USD',
  testMode: true,
  stripeEnabled: false,
  stripePublishableKey: '',
  stripeSecretKey: '',
  stripeWebhookSecret: '',
  paypalEnabled: false,
  paypalClientId: '',
  paypalClientSecret: '',
  paypalWebhookId: '',
  otherEnabled: false,
  otherProviderName: '',
  otherPublicKey: '',
  otherSecretKey: '',
  otherInstructions: '',
};

export default function AdminSettings() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [payment, setPayment] = useState(PAYMENT_DEFAULTS);
  const [savingPayment, setSavingPayment] = useState(false);
  const [tab, setTab] = useState('url');
  const fileRef = useRef();
  const { toast } = useToast();
  const { refresh: refreshSettings } = useSettings();
  const { isSuperAdmin } = useAuth();

  const load = () => api.settings().then(setForm).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!isSuperAdmin) return;
    api.admin.paymentGet().then(d => setPayment({ ...PAYMENT_DEFAULTS, ...d })).catch(() => {});
  }, [isSuperAdmin]);

  const save = async () => {
    setSaving(true);
    try {
      await api.admin.settingsUpdate({
        brandName: form.brandName,
        heroTitle: form.heroTitle,
        heroTagline: form.heroTagline,
        heroSubtitle: form.heroSubtitle,
        logoUrl: form.logoUrl || null,
      });
      toast({ title: 'Settings saved' });
      refreshSettings();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
    setSaving(false);
  };

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast({ title: 'File too large (max 3MB)', variant: 'destructive' }); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setForm(f => ({ ...f, logoUrl: data.url }));
      toast({ title: 'Logo uploaded', description: 'Auto-saved. Refresh other tabs to see updated logo.' });
      refreshSettings();
      load();
    } catch (e) {
      toast({ title: 'Upload failed', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeLogo = async () => {
    setForm(f => ({ ...f, logoUrl: '' }));
    try {
      await api.admin.settingsUpdate({ logoUrl: null });
      toast({ title: 'Logo cleared' });
      refreshSettings();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const downloadSource = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/download/source`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error('Server rejected the request');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'skillbridge-guru.tar.gz';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: 'Download started', description: 'skillbridge-guru.tar.gz' });
    } catch (e) {
      toast({ title: 'Download failed', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const savePayment = async () => {
    setSavingPayment(true);
    try {
      const payload = { ...payment, currency: (payment.currency || 'USD').toUpperCase() };
      const saved = await api.admin.paymentUpdate(payload);
      setPayment({ ...PAYMENT_DEFAULTS, ...saved });
      toast({ title: 'Payment options saved', description: 'Secret keys are stored server-side and masked after saving.' });
    } catch (e) {
      toast({ title: 'Payment save failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally {
      setSavingPayment(false);
    }
  };

  const setPay = (key, value) => setPayment(prev => ({ ...prev, [key]: value }));

  const logoUrl = resolveAsset(form.logoUrl);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold">Site settings</h1>
        <p className="text-gray-500 text-sm mt-1">Update branding, logo and hero content shown across the site.</p>
      </div>

      {/* Logo section */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="font-bold text-lg">Brand logo</h2>
            <p className="text-xs text-gray-500 mt-0.5">Appears in navbar, footer, login, signup and admin sidebar.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setTab('url')} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 ${tab === 'url' ? 'bg-[#6b47dc] text-white' : 'bg-gray-100 text-gray-700'}`}><Link2 size={13} /> Paste URL</button>
            <button onClick={() => setTab('upload')} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 ${tab === 'upload' ? 'bg-[#6b47dc] text-white' : 'bg-gray-100 text-gray-700'}`}><Upload size={13} /> Upload file</button>
          </div>
        </div>

        <div className="grid md:grid-cols-[220px_1fr] gap-6 items-start">
          <div className="bg-gradient-to-br from-purple-50 to-orange-50 border border-dashed border-purple-200 rounded-xl p-6 aspect-square flex items-center justify-center relative">
            {logoUrl ? (
              <>
                <img src={logoUrl} alt="logo preview" className="max-w-full max-h-full object-contain" />
                <button onClick={removeLogo} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white shadow grid place-items-center text-red-600 hover:bg-red-50" title="Remove logo"><X size={14} /></button>
              </>
            ) : (
              <div className="text-center text-gray-400">
                <ImageIcon size={32} className="mx-auto mb-2" />
                <div className="text-xs font-semibold">No custom logo</div>
                <div className="text-[10px]">Default gradient badge will be used</div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            {tab === 'url' && (
              <div>
                <label className="text-xs font-bold text-gray-500">Logo image URL</label>
                <div className="flex gap-2 mt-1">
                  <input
                    placeholder="https://your-cdn.com/logo.png"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]"
                    value={form.logoUrl || ''}
                    onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                  />
                  <Button onClick={save} disabled={saving} className="bg-[#6b47dc] hover:bg-[#5a3ac0]">{saving ? 'Saving...' : 'Save'}</Button>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">Paste any publicly reachable image URL (PNG, JPG, SVG, WebP). Recommended: square, transparent background.</p>
              </div>
            )}
            {tab === 'upload' && (
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-2">Upload from your computer</label>
                <input ref={fileRef} onChange={onFilePick} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif" className="hidden" />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#6b47dc] hover:bg-[#5a3ac0]">
                  <Upload size={14} className="mr-1.5" /> {uploading ? 'Uploading...' : 'Choose image file'}
                </Button>
                <p className="text-[11px] text-gray-500 mt-2">Max 3MB. Accepted: PNG, JPG, SVG, WebP, GIF. Uploaded logo is saved automatically.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Branding & hero */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 space-y-4">
        <h2 className="font-bold text-lg">Branding &amp; hero content</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="text-xs font-bold text-gray-500">Brand name</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.brandName || ''} onChange={(e) => setForm({ ...form, brandName: e.target.value })} /></div>
          <div><label className="text-xs font-bold text-gray-500">Hero title</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.heroTitle || ''} onChange={(e) => setForm({ ...form, heroTitle: e.target.value })} /></div>
          <div className="md:col-span-2"><label className="text-xs font-bold text-gray-500">Hero tagline (gradient part)</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.heroTagline || ''} onChange={(e) => setForm({ ...form, heroTagline: e.target.value })} /></div>
          <div className="md:col-span-2"><label className="text-xs font-bold text-gray-500">Hero subtitle</label><textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.heroSubtitle || ''} onChange={(e) => setForm({ ...form, heroSubtitle: e.target.value })} /></div>
        </div>
        <div>
          <Button onClick={save} disabled={saving} className="bg-[#6b47dc] hover:bg-[#5a3ac0]"><Save size={16} className="mr-1" /> {saving ? 'Saving...' : 'Save all settings'}</Button>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="bg-white p-6 rounded-2xl border border-gray-200 space-y-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2"><CreditCard size={20} className="text-[#6b47dc]" /> Payment options</h2>
              <p className="text-xs text-gray-500 mt-1">Superadmin-only configuration for checkout providers. Public pages can read only publishable keys and enabled providers.</p>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-800">
              <Lock size={14} /> Secret fields are masked after save
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-100 hover:border-purple-200 md:col-span-2">
              <input type="checkbox" className="accent-[#6b47dc] w-5 h-5" checked={!!payment.enabled} onChange={(e) => setPay('enabled', e.target.checked)} />
              <div>
                <div className="font-bold">Enable payments</div>
                <div className="text-xs text-gray-500">Turn on only after provider credentials are tested.</div>
              </div>
            </label>
            <div>
              <label className="text-xs font-bold text-gray-500">Default provider</label>
              <select value={payment.defaultProvider || 'stripe'} onChange={(e) => setPay('defaultProvider', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc] bg-white">
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500">Currency</label>
              <input value={payment.currency || ''} onChange={(e) => setPay('currency', e.target.value)} placeholder="USD" className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc] uppercase" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="accent-[#6b47dc]" checked={!!payment.testMode} onChange={(e) => setPay('testMode', e.target.checked)} /> Test mode / sandbox
          </label>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <label className="flex items-center gap-2 font-bold"><input type="checkbox" className="accent-[#6b47dc]" checked={!!payment.stripeEnabled} onChange={(e) => setPay('stripeEnabled', e.target.checked)} /> Stripe</label>
              <div><label className="text-xs font-bold text-gray-500">Publishable key</label><input value={payment.stripePublishableKey || ''} onChange={(e) => setPay('stripePublishableKey', e.target.value)} placeholder="pk_test_..." className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" /></div>
              <div><label className="text-xs font-bold text-gray-500">Secret key</label><input type="password" value={payment.stripeSecretKey || ''} onChange={(e) => setPay('stripeSecretKey', e.target.value)} placeholder="sk_test_..." className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" /></div>
              <div><label className="text-xs font-bold text-gray-500">Webhook signing secret</label><input type="password" value={payment.stripeWebhookSecret || ''} onChange={(e) => setPay('stripeWebhookSecret', e.target.value)} placeholder="whsec_..." className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" /></div>
            </div>

            <div className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <label className="flex items-center gap-2 font-bold"><input type="checkbox" className="accent-[#6b47dc]" checked={!!payment.paypalEnabled} onChange={(e) => setPay('paypalEnabled', e.target.checked)} /> PayPal</label>
              <div><label className="text-xs font-bold text-gray-500">Client ID</label><input value={payment.paypalClientId || ''} onChange={(e) => setPay('paypalClientId', e.target.value)} placeholder="PayPal app client ID" className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" /></div>
              <div><label className="text-xs font-bold text-gray-500">Client secret</label><input type="password" value={payment.paypalClientSecret || ''} onChange={(e) => setPay('paypalClientSecret', e.target.value)} placeholder="PayPal app secret" className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" /></div>
              <div><label className="text-xs font-bold text-gray-500">Webhook ID</label><input type="password" value={payment.paypalWebhookId || ''} onChange={(e) => setPay('paypalWebhookId', e.target.value)} placeholder="PayPal webhook ID" className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" /></div>
            </div>

            <div className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <label className="flex items-center gap-2 font-bold"><input type="checkbox" className="accent-[#6b47dc]" checked={!!payment.otherEnabled} onChange={(e) => setPay('otherEnabled', e.target.checked)} /> Other provider</label>
              <div><label className="text-xs font-bold text-gray-500">Provider name</label><input value={payment.otherProviderName || ''} onChange={(e) => setPay('otherProviderName', e.target.value)} placeholder="Razorpay, Square, bank transfer..." className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" /></div>
              <div><label className="text-xs font-bold text-gray-500">Public key / merchant ID</label><input value={payment.otherPublicKey || ''} onChange={(e) => setPay('otherPublicKey', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" /></div>
              <div><label className="text-xs font-bold text-gray-500">Secret key</label><input type="password" value={payment.otherSecretKey || ''} onChange={(e) => setPay('otherSecretKey', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" /></div>
              <div><label className="text-xs font-bold text-gray-500">Instructions / notes</label><textarea rows={3} value={payment.otherInstructions || ''} onChange={(e) => setPay('otherInstructions', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" /></div>
            </div>
          </div>

          <div className="rounded-xl bg-purple-50 border border-purple-100 p-4 text-sm text-gray-700 flex items-start gap-2">
            <Info size={16} className="text-[#6b47dc] mt-0.5 flex-shrink-0" />
            <div>This saves provider credentials and exposes a safe public configuration endpoint. Actual charge creation, webhook verification and checkout UI should be added separately for the selected provider before accepting real payments.</div>
          </div>

          <Button onClick={savePayment} disabled={savingPayment} className="bg-[#6b47dc] hover:bg-[#5a3ac0]">
            <Save size={16} className="mr-1" /> {savingPayment ? 'Saving...' : 'Save payment options'}
          </Button>
        </div>
      )}

      {/* Download source */}
      <div className="bg-gradient-to-br from-[#6b47dc] via-[#7c3aed] to-[#ff5722] p-6 rounded-2xl text-white">
        <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FileArchive size={22} />
              <h2 className="font-extrabold text-lg">Download complete source code</h2>
            </div>
            <p className="text-sm opacity-90">Get the full frontend + backend + deploy scripts + README as a single <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded">.tar.gz</span> archive. Excludes <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded">node_modules</span>, <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded">.env</span>, git history and build artifacts.</p>
            <p className="text-xs opacity-80 mt-2">Contains: <span className="font-mono">backend/</span>, <span className="font-mono">frontend/</span>, <span className="font-mono">deploy/install.sh</span>, <span className="font-mono">README.md</span></p>
          </div>
          <Button onClick={downloadSource} disabled={downloading} className="bg-white text-[#6b47dc] hover:bg-gray-100 font-bold h-12 px-6 shadow-xl">
            <Download size={18} className="mr-2" /> {downloading ? 'Preparing...' : 'Download source'}
          </Button>
        </div>
      </div>
    </div>
  );
}
